import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';

import type { ButtonClick } from '@snowit/icetypes/events';
import type { ModalComponent } from '../../types';

import Button from '@snowit/ui/Buttons/Button';
import ButtonIcon from '@snowit/ui/Buttons/ButtonIcon';
import LoaderImage from '@snowit/ui/Loaders/LoaderImage';
import Typo from '@snowit/ui/Typography/Typo';
import Motion, { m } from '@snowit/ui/utils/Motion';
import testId from '@snowit/utils/testId';

import { main, overlay } from '../../animations';

import cx from 'classnames';
import s from './Modal.module.scss';

import { AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

const Modal = (props: ModalComponent) => {
  const {
    theme = 'default',
    body,
    classNames,
    close = { theme: 'icon' },
    footer,
    header,
    id,
    onClose,
    onOpen,
    opened,
    otherAction,
    size = 'df',
    test,
    loading = false,
  } = props;

  const parent: Element = document.getElementById('snowit-widget') ?? document.body;
  const contentRef = useRef<HTMLDivElement>(null);
  const lastActiveElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (opened) {
      if (!lastActiveElement.current && document.activeElement) {
        lastActiveElement.current = document.activeElement as HTMLElement;
      }
      contentRef.current?.focus();
    } else {
      lastActiveElement.current?.focus();
      lastActiveElement.current = null;
    }
  }, [opened]);

  useEffect(() => {
    document.body.style.overflowY = opened ? 'hidden' : '';
    if (opened && typeof onOpen === 'function') {
      onOpen();
    }
  }, [opened]);

  const handleClose = (args: ButtonClick) => {
    if (typeof onClose === 'function') {
      onClose(id);
    }
    args.event.stopPropagation();
  };

  const renderHeaderContent = () => {
    if (!header) return null;

    const headerContent = typeof header === 'function' ? header() : header || '';

    if (typeof headerContent === 'string') {
      return (
        <div className={cx(s.contentHeader, classNames?.headerContent)}>
          <Typo
            text={headerContent}
            tag={'h2'}
            size={theme === 'default' ? 'df' : 'xxl'}
            weight={'medium'}
            color={'text'}
          />
        </div>
      );
    }

    return <div className={cx(s.contentHeader, classNames?.headerContent)}>{headerContent}</div>;
  };

  const renderClose = () => {
    if (!close) return null;

    if (close.theme === 'button') {
      return (
        <Button
          classNames={{ button: cx(s.close, classNames?.close) }}
          label={close.label}
          icon={'close'}
          theme={'fill'}
          bgColor={'text'}
          size={'db'}
          onClick={handleClose}
          test={'close_btn'}
        />
      );
    }

    return (
      <ButtonIcon
        classNames={{ button: cx(s.close, classNames?.close) }}
        icon={'close'}
        onClick={handleClose}
        theme={'ghost'}
        size={'db'}
        color={'text'}
        test={'close_btn'}
      />
    );
  };

  const renderOtherAction = () => {
    if (!otherAction) return null;

    const otherActionContent = typeof otherAction === 'function' ? otherAction() : otherAction;

    return <div className={cx(s.otherAction, classNames?.otherAction)}>{otherActionContent}</div>;
  };

  const renderHeader = () => {
    if (!header && !close) return null;
    return (
      <header
        className={cx(s.header, classNames?.header, {
          [s.default]: typeof header === 'string' || 'function',
        })}
      >
        {renderOtherAction()}
        {theme === 'default' && renderHeaderContent()}
        {renderClose()}
      </header>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <LoaderImage
        // classNames={classNames}
        // img={img || fallbackImg}
        // title={title ?? t('lzy.loading.title')}
        // text={text ?? t('lzy.loading.text')}
        />
      );
    }
    const bodyContent = typeof body === 'function' ? body() : body;
    return (
      <div className={cx(s.body, classNames?.body)}>
        {theme !== 'default' && renderHeaderContent()}
        {bodyContent}
      </div>
    );
  };

  const renderFooter = () => {
    if (!footer) return null;
    const footerContent = typeof footer === 'function' ? footer() : footer;
    if (!footerContent) return null;
    return <footer className={cx(s.footer, classNames?.footer)}>{footerContent}</footer>;
  };

  return createPortal(
    <Motion>
      <AnimatePresence>
        {opened && (
          <m.div
            className={cx(s.overlay, classNames?.overlay, {
              [s[theme]]: true,
              [s[size]]: true,
              [s.noHeader]: !header && !close,
              [s.noFooter]: !footer,
              [s.onlyBody]: !header && !close && !footer,
            })}
            initial='start'
            animate='open'
            exit='close'
            variants={overlay.variants}
            transition={overlay.transition}
            {...testId(`${test}_modal_wrapper`)}
          >
            <m.div
              ref={contentRef}
              className={cx(s.modal, classNames?.modal, {})}
              initial='start'
              animate='open'
              exit='close'
              variants={main.variants}
              transition={main.transition}
              role={'dialog'}
              tabIndex={-1}
              aria-modal={true}
              {...testId(`${test}_modal_content`)}
            >
              {renderHeader()}
              {renderBody()}
              {renderFooter()}
            </m.div>
          </m.div>
        )}
      </AnimatePresence>
    </Motion>,
    parent,
  ) as ReactElement;
};

export default Modal;
